import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu'
import { CheckOutlined } from 'src/icons/antd-compat'

import type { DropdownProps, MenuProps } from '../antd-props'
import { OverlaySurfaceProvider } from '../overlay/overlaySurface'
import {
  AntdPlacement,
  normaliseTriggers,
  omitTransient,
  toRadixSideAlign,
} from '../overlay/placement'
import { refCapableTrigger } from '../overlay/refCapableTrigger'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Dropdown` seam — antd's Dropdown rebuilt on
 * the Radix DropdownMenu (`src/components/ui/dropdown-menu`) behind antd's own
 * `DropdownProps`. `./index.tsx` is the dispatcher that chooses between this
 * and `./Dropdown.antd`; nothing else imports this file, and nothing reaches
 * it until someone flips the design toggle.
 *
 * The 29 call sites are entirely on antd v5 conventions — `menu={{ items }}`,
 * never the legacy v4 `overlay` — which is what makes this portable at all.
 *
 * WHAT IS MAPPED
 *   • `menu={{ items, onClick, selectedKeys, style }}` → Radix items. An item
 *     carries `label`, `icon`, `onClick`, `danger`, `disabled` and `style`;
 *     `type: 'divider'` and `type: 'group'` become a separator and a labelled
 *     group.
 *   • `dropdownRender` (10 call sites; Radix has no analogue) → its node is
 *     rendered as-is inside the Content, and close-auto-focus is suppressed so
 *     a custom panel — an inner antd `<Menu>`, the rich-text colour pickers —
 *     keeps its own focus and keys.
 *   • `trigger` click (20 call sites) or hover; controlled `open`/
 *     `onOpenChange`; `placement` (antd's default is `bottomLeft`);
 *     `disabled`; `overlayClassName` / `overlayStyle`.
 *   • `getPopupContainer`, DEFAULTING TO THE TRIGGER'S PARENT — the same
 *     default `./Dropdown.antd` has always had, and kept for the same reason
 *     `../Select/Select.shadcn` keeps its own: five call sites pass their own
 *     container precisely to escape it, and one of them
 *     (`route-studio` `WhatIfDropdown`) documents a founder-reported flicker
 *     bug caused by the default. Re-homing all 29 to `document.body` under a
 *     toggle whose premise is behaviour preservation is not this PR's call.
 *
 * WHAT IS NOT — named rather than pretended:
 *   • `overlay` (v4) and `Dropdown.Button`. No call site uses either; the
 *     static is kept on the export so the shape is unchanged, and
 *     `./index.tsx` says which component it resolves to in each mode.
 *   • Submenus. No `menu.items` entry in this repo carries `children` on a
 *     non-group item, so the Radix Sub* parts were not brought in at all
 *     (`src/components/ui/dropdown-menu.tsx` records that).
 *   • `arrow`, `destroyPopupOnHide`, `autoAdjustOverflow`, `autoFocus`,
 *     `align`, `openClassName`, `mouseEnterDelay`/`mouseLeaveDelay`,
 *     `transitionName`. They are accepted and dropped so none reaches the DOM;
 *     no call site sets any of them. The hover CLOSE delay below is a fixed
 *     100 ms rather than `mouseLeaveDelay`, for the same reason.
 *
 * Ported from the `update-design` reference branch, with the trigger node held
 * in STATE rather than a bare ref: the reference computes the portal container
 * during render from a ref that is still `null` on the first pass, so a
 * dropdown rendered already-open (`open` controlled from mount) portalled to
 * the body instead of its container.
 * ------------------------------------------------------------------------- */

type AntdMenuItem = NonNullable<MenuProps['items']>[number]

/** How long a hover-triggered menu waits before closing, in ms. */
const HOVER_CLOSE_DELAY = 100

function renderItems(
  items: MenuProps['items'],
  menuOnClick: MenuProps['onClick'],
  selectedKeys: React.Key[] | undefined
): React.ReactNode {
  if (!items) return null
  return items.map((item, index) => {
    if (!item) return null
    const anyItem = item as AntdMenuItem & {
      type?: string
      label?: React.ReactNode
      icon?: React.ReactNode
      danger?: boolean
      disabled?: boolean
      children?: MenuProps['items']
      onClick?: (info: { key: React.Key; domEvent: Event }) => void
      style?: React.CSSProperties
    }
    const key = anyItem.key ?? index

    if (anyItem.type === 'divider') {
      return <DropdownMenuSeparator key={`div-${key}`} />
    }

    if (anyItem.type === 'group') {
      return (
        <DropdownMenuGroup key={`grp-${key}`}>
          {anyItem.label != null && (
            <DropdownMenuLabel>{anyItem.label}</DropdownMenuLabel>
          )}
          {renderItems(anyItem.children, menuOnClick, selectedKeys)}
        </DropdownMenuGroup>
      )
    }

    const selected = selectedKeys?.some((k) => `${k}` === `${key}`)

    return (
      <DropdownMenuItem
        key={key}
        disabled={anyItem.disabled}
        variant={anyItem.danger ? 'destructive' : 'default'}
        style={anyItem.style}
        onSelect={(event) => {
          const domEvent = event as unknown as Event
          anyItem.onClick?.({ key, domEvent })
          menuOnClick?.({
            key: `${key}`,
            domEvent,
            keyPath: [`${key}`],
            selectedKeys: undefined,
          } as never)
        }}
      >
        {anyItem.icon}
        <span className="flex-1">{anyItem.label}</span>
        {selected && <CheckOutlined className="ml-2" aria-hidden />}
      </DropdownMenuItem>
    )
  })
}

const DropdownShadcn: FC<DropdownProps> = ({
  children,
  menu,
  dropdownRender,
  trigger,
  placement,
  open,
  onOpenChange,
  disabled,
  getPopupContainer,
  overlayClassName,
  overlayStyle,
  // accepted, then dropped so they never reach the DOM — see the header
  destroyPopupOnHide: _destroyPopupOnHide,
  arrow: _arrow,
  autoAdjustOverflow: _autoAdjustOverflow,
  autoFocus: _autoFocus,
  align: _align,
  openClassName: _openClassName,
  mouseEnterDelay: _mouseEnterDelay,
  mouseLeaveDelay: _mouseLeaveDelay,
  transitionName: _transitionName,
  className,
  ...rest
}) => {
  const triggers = normaliseTriggers(
    trigger as Parameters<typeof normaliseTriggers>[0],
    'hover'
  )
  const hoverOnly = triggers.has('hover') && !triggers.has('click') && !disabled

  const controlled = open !== undefined
  const [internalOpen, setInternalOpen] = useState(false)
  const actualOpen = controlled ? Boolean(open) : internalOpen
  const setOpen = useCallback(
    (next: boolean) => {
      if (!controlled) setInternalOpen(next)
      onOpenChange?.(next, { source: 'trigger' } as never)
    },
    [controlled, onOpenChange]
  )

  // State, not a bare ref: the container is resolved DURING render, and a ref
  // is still `null` on the first pass. See the note in the header.
  const [triggerNode, setTriggerNode] = useState<HTMLElement | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])
  useEffect(() => clearTimer, [clearTimer])

  const { side, align } = toRadixSideAlign(
    placement as AntdPlacement | undefined,
    'bottomLeft'
  )

  // antd calls `getPopupContainer` lazily, once the popup opens and the trigger
  // node exists. Here it can run on the first render when there is no node yet,
  // and calling it with `null` crashes a consumer written as
  // `(triggerNode) => triggerNode.parentNode`.
  const container = (() => {
    if (typeof getPopupContainer === 'function') {
      return triggerNode
        ? getPopupContainer(triggerNode) ?? undefined
        : undefined
    }
    // `./Dropdown.antd`'s default: render into the trigger's parent element.
    return triggerNode?.parentElement ?? undefined
  })()

  const usesCustomRender = typeof dropdownRender === 'function'

  const triggerChild = React.isValidElement(children) ? (
    children
  ) : (
    <span>{children}</span>
  )

  const childProps = (triggerChild.props ?? {}) as {
    onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void
    onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void
  }

  const hoverHandlers = hoverOnly
    ? {
        onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
          childProps.onMouseEnter?.(event)
          clearTimer()
          setOpen(true)
        },
        onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
          childProps.onMouseLeave?.(event)
          clearTimer()
          closeTimer.current = setTimeout(
            () => setOpen(false),
            HOVER_CLOSE_DELAY
          )
        },
      }
    : {}

  const menuBody = (
    <>
      {renderItems(
        menu?.items,
        menu?.onClick,
        menu?.selectedKeys as React.Key[] | undefined
      )}
    </>
  )

  return (
    // `modal={false}` is NOT a detail. Radix's DropdownMenu defaults to modal:
    // it `aria-hidden`s the rest of the page, sets `pointer-events: none` on
    // the body and locks scrolling while the menu is open, and it pulls focus
    // into the menu on mount. antd's Dropdown does none of that — an open menu
    // there leaves the page live underneath. Left at the default, an open menu
    // dismissed any Select popup that happened to be open beside it (found in
    // the browser, not by a test) and froze the page behind it.
    <DropdownMenu open={actualOpen} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger
        asChild
        disabled={disabled}
        ref={setTriggerNode as never}
        // antd's hover Dropdown ignores a click on the trigger: the menu the
        // hover opened stays open. Radix's trigger toggles on pointer-down, so
        // without this the natural next action — clicking the `…` button the
        // pointer is already over — closes the menu again. Radix composes its
        // own handler behind `defaultPrevented`, so preventing the default here
        // suppresses only the toggle; focus is restored by hand because
        // pointer-down's default is what focuses the trigger. A
        // `trigger={['click']}` Dropdown is untouched and keeps toggling.
        onPointerDown={
          hoverOnly
            ? (event: React.PointerEvent<HTMLElement>) => {
                if (event.button !== 0 || event.ctrlKey) return
                event.preventDefault()
                event.currentTarget.focus?.()
              }
            : undefined
        }
      >
        {refCapableTrigger(
          triggerChild,
          hoverHandlers as Record<string, unknown>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        {...omitTransient(rest as Record<string, unknown>)}
        side={side}
        align={align}
        className={overlayClassName ?? className}
        style={{
          ...(overlayStyle as React.CSSProperties),
          ...(menu?.style as React.CSSProperties),
        }}
        container={container}
        // The trigger sits OUTSIDE the portalled content, so Radix's dismissable
        // layer reads BOTH the pointer-down on it and the focus that follows as
        // outside interactions and closes the menu — the other half of the
        // hover-trigger click above. antd's hover Dropdown keeps the menu open,
        // so an interaction with the trigger alone is suppressed here; every
        // other outside pointer-down or focus still dismisses.
        onInteractOutside={
          hoverOnly
            ? (event) => {
                const target = (event.detail?.originalEvent?.target ??
                  null) as Node | null
                if (target && triggerNode?.contains(target)) {
                  event.preventDefault()
                }
              }
            : undefined
        }
        onMouseEnter={hoverOnly ? clearTimer : undefined}
        onMouseLeave={
          hoverOnly
            ? () => {
                closeTimer.current = setTimeout(
                  () => setOpen(false),
                  HOVER_CLOSE_DELAY
                )
              }
            : undefined
        }
        // A custom render — or an inner antd `<Menu>` — owns its own focus and
        // keyboard, so Radix must not take it in either direction. OPEN matters
        // most: a panel whose `open` is driven by an input OUTSIDE it would
        // lose the caret mid-word when Radix pulls focus into the menu on
        // mount. antd's Dropdown focuses nothing, so this is parity, not
        // preference.
        onOpenAutoFocus={
          usesCustomRender
            ? (event: Event) => event.preventDefault()
            : undefined
        }
        onCloseAutoFocus={
          usesCustomRender
            ? (event: Event) => event.preventDefault()
            : undefined
        }
      >
        {/* This content IS the floating panel, so anything rendered inside it
            — most often a seam `<Menu>` handed in through `dropdownRender` —
            must not paint a panel of its own. See `../overlay/overlaySurface`
            for why that is a context rather than a prop at each call site. */}
        <OverlaySurfaceProvider>
          {usesCustomRender ? dropdownRender?.(menuBody) : menuBody}
        </OverlaySurfaceProvider>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

DropdownShadcn.displayName = 'Dropdown'

export { DropdownShadcn }
